import React, { useState } from "react";
import { X, Lock, Loader2, KeyRound, Check } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

/**
 * PaywallModal — institutional single-tier.
 *
 * Vexent Research operates on ONE tier: Professional at $2,000 / year.
 * Two paths in:
 *   1. Subscribe — Stripe checkout for the $2,000/yr Professional plan.
 *   2. Access Code — free-pass code issued monthly to invited clients,
 *      redeemed against POST /api/access-code/redeem.  Correct codes
 *      grant a 32-day session with tier=professional.
 *
 * This modal deliberately has no free / premium options anymore — those
 * tiers were retired in the institutional pivot.
 */

const PaywallModal = ({ open, onClose }) => {
  const [tab, setTab] = useState("subscribe");   // "subscribe" | "code"
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [redeemed, setRedeemed] = useState(false);

  if (!open) return null;

  const startCheckout = async () => {
    setLoading(true);
    try {
      const { url } = await api.post("/stripe/create-checkout-session", {
        tier: "professional",
        successUrl: window.location.origin + "/?checkout=success",
        cancelUrl: window.location.origin + "/?checkout=cancelled",
      });
      if (url) {
        window.location.href = url;
        return;
      }
      toast.error("Could not start checkout. Please try again.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed. Try again in a moment.");
    } finally {
      setLoading(false);
    }
  };

  const submitCode = async (e) => {
    e.preventDefault();
    const clean = code.trim();
    if (!clean) return;
    setLoading(true);
    try {
      await api.post("/access-code/redeem", { code: clean });
      setRedeemed(true);
      toast.success("Access granted. Welcome.");
      setTimeout(() => { window.location.reload(); }, 1200);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401) toast.error("That code is not valid.");
      else if (status === 503) toast.error("Access codes are not currently configured.");
      else toast.error("Something went wrong. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-[#0B1F33]/80 backdrop-blur-sm p-4"
      data-testid="paywall-modal"
      onClick={onClose}
    >
      <div
        className="bg-white w-full max-w-[560px] shadow-2xl border-t-4 border-[#B8E9F1] relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          data-testid="paywall-close-btn"
          className="absolute top-3 right-3 p-2 text-neutral-500 hover:text-[#0B1F33]"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="px-8 pt-10 pb-2">
          <div className="text-[11px] uppercase tracking-[0.28em] text-[#0891B2] font-condensed font-semibold">
            Vexent Research · Professional
          </div>
          <h2 className="mt-3 font-serif-display text-[26px] leading-[1.15] font-bold text-[#0B1F33]">
            Institutional access to Vexent Research.
          </h2>
          <p className="mt-3 text-[13.5px] text-neutral-600 leading-relaxed">
            One tier. One price. Full read + PDF + audio for every published report.
          </p>
        </div>

        <div className="px-8 pt-6 flex gap-2 border-b border-[#D7DEE5]">
          <TabButton active={tab === "subscribe"} onClick={() => setTab("subscribe")} testid="tab-subscribe">
            Subscribe
          </TabButton>
          <TabButton active={tab === "code"} onClick={() => setTab("code")} testid="tab-access-code">
            Access Code
          </TabButton>
        </div>

        <div className="px-8 py-8">
          {tab === "subscribe" && (
            <div>
              <div className="flex items-baseline gap-2">
                <div className="font-serif-display text-[36px] font-bold text-[#0B1F33]">$2,000</div>
                <div className="text-[13px] uppercase tracking-[0.22em] text-neutral-500 font-condensed font-semibold">/ year</div>
              </div>
              <ul className="mt-6 space-y-2.5 text-[13.5px] text-neutral-700 leading-[1.6]">
                {[
                  "Full text and downloadable PDF for every Vexent report",
                  "Audio narration of every report",
                  "Early access to reports in preparation",
                  "Analyst notes and update briefs between reports",
                ].map((b) => (
                  <li key={b} className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-[#0891B2] shrink-0 mt-0.5" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
              <button
                onClick={startCheckout}
                disabled={loading}
                data-testid="paywall-subscribe-btn"
                className="mt-8 w-full px-6 py-4 bg-[#0B1F33] hover:bg-[#132f57] disabled:opacity-60 text-white uppercase tracking-[0.22em] text-[11px] font-semibold transition-colors inline-flex items-center justify-center gap-2"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-3.5 h-3.5" />}
                {loading ? "Redirecting…" : "Continue to Stripe Checkout"}
              </button>
              <p className="mt-3 text-[11px] text-neutral-500 text-center">
                Secure payment via Stripe. Cancel anytime from your account.
              </p>
            </div>
          )}

          {tab === "code" && (
            <form onSubmit={submitCode}>
              <label className="block text-[11px] uppercase tracking-[0.22em] text-neutral-500 font-condensed font-semibold">
                Enter your access code
              </label>
              <div className="mt-2 relative">
                <KeyRound className="w-4 h-4 text-neutral-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  autoFocus
                  placeholder="VEXENT-XXXX-XXXXXX"
                  data-testid="access-code-input"
                  className="w-full pl-10 pr-4 py-3.5 border border-neutral-300 focus:border-[#0B1F33] focus:outline-none text-[15px] tracking-wider font-mono uppercase"
                />
              </div>
              <p className="mt-3 text-[12px] text-neutral-500 leading-relaxed">
                Access codes are issued to invited institutional clients and rotated monthly. Codes grant a 32-day Professional session.
              </p>
              <button
                type="submit"
                disabled={loading || !code.trim() || redeemed}
                data-testid="access-code-submit-btn"
                className="mt-6 w-full px-6 py-4 bg-[#0B1F33] hover:bg-[#132f57] disabled:opacity-60 text-white uppercase tracking-[0.22em] text-[11px] font-semibold transition-colors inline-flex items-center justify-center gap-2"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-3.5 h-3.5" />}
                {redeemed ? "Access granted…" : loading ? "Verifying…" : "Redeem Code"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

const TabButton = ({ active, onClick, testid, children }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className={`px-4 py-3 text-[11px] uppercase tracking-[0.22em] font-condensed font-semibold border-b-2 transition-colors ${
      active
        ? "border-[#0B1F33] text-[#0B1F33]"
        : "border-transparent text-neutral-500 hover:text-[#0B1F33]"
    }`}
  >
    {children}
  </button>
);

export default PaywallModal;
